import { possessive } from '@shared';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useIsMobile,
} from '@design-system';
import { Info, SplitSquareHorizontal } from 'lucide-react';

import { CurrencyInput, IntegerInput, PercentInput } from '@/shared/components/numeric-input';
import { DisabledByCareTooltip } from '@/shared/components/disabled-by-care-tooltip';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { PartnerAllocationSection } from '@/components/funding-sources-coordination/partner-allocation-section';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { Input } from '@/components/ui/input';
import {
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  SELF_FUNDING_ASSET_TYPES,
  filterForRetirementIncomeExpanded,
} from '@/features/guided-financial-planning/config';
import { CalcFormulaSheetContent } from '@/features/guided-financial-planning/components/calc-formula-sheet-content';
import {
  YearPresetSelect,
  CarePhaseYearSelect,
} from '@/features/guided-financial-planning/components/year-select';

import { ErrorMessage, FieldLabel, HeaderPreview, ReadOnlyContent } from './components';
import { useSelfFundingModalForm } from './hooks';

interface SelfFundingDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  fundingSourceId?: string;
}

export function SelfFundingDetailsModal({
  open,
  setOpen,
  fundingSourceId,
}: SelfFundingDetailsModalProps) {
  const isMobile = useIsMobile();
  const form = useSelfFundingModalForm({ open, setOpen, fundingSourceId });

  const {
    selfFunding,
    partnerFirstName,
    clientName,
    selfFundingState,
    setSelfFundingState,
    hasContributionStopToggle,
    selectedAssetType,
    yearlyContributionValue,
    formErrors,
    formIsValid,
    hasRequiredFieldsErrors,
    mirrorChecked,
    setMirrorChecked,
    allocationOption,
    setAllocationOption,
    customClientPercent,
    setCustomClientPercent,
    showWarning,
    showFormulaSheet,
    setShowFormulaSheet,
    isIncomeStream,
    isOther,
    isSpecificAssetType,
    isReadOnlyRecipient,
    isExistingMirror,
    fieldLabels,
    requiredKeys,
    incomeReferenceAgeError,
    showPartnerSection,
    showPreview,
    projectedValue,
    ageAtLtc,
    formulaInfo,
    formulaAssetTypeId,
    assetLabel,
    readOnlyAccountLabel,
    usedAccountTypes,
    totalAssets,
    flags,
    carePhaseYears,
    ltcEndYear,
    ltcAtYear,
    ltcAtAge,
    clientHasStartedLtc,
    careHasStarted,
    handleChangeSelfFunding,
    handleChangeSelfFundingName,
    handleAssetTypeChange,
    handleToggleContributionStop,
    handleToggleDrawdown,
    handleChangeYearlyContribution,
    handleClickSubmit,
    onClose,
    handleConfirmClose,
    handleCancelClose,
  } = form;

  const formContent = (
    <div className="space-y-2">
      {isReadOnlyRecipient && partnerFirstName && (
        <Alert
          variant="info"
          className="gap-1! mb-4 rounded-md p-2.5 [&>svg]:translate-y-px"
        >
          <Info className="size-3.5!" />
          <AlertDescription className="text-xs">
            This account is shared from {possessive(partnerFirstName)} plan. Only {partnerFirstName} can
            edit it.
          </AlertDescription>
        </Alert>
      )}
      {isReadOnlyRecipient && (
        <ReadOnlyContent
          accountLabel={readOnlyAccountLabel}
          isIncomeStream={isIncomeStream}
          source={selfFunding as SelfFundingSource | undefined}
        />
      )}
      {!isReadOnlyRecipient && (
        <fieldset
          className="m-0 space-y-5 border-0 p-0"
          data-intercom-target="Self Funding Fields"
        >
          {isExistingMirror && partnerFirstName && (
            <Alert
              variant="warning"
              className="gap-1! mb-4 rounded-md p-2.5 [&>svg]:translate-y-px"
            >
              <SplitSquareHorizontal className="size-3.5!" />
              <AlertDescription className="text-xs">
                You&apos;re editing the combined total for {clientName} and {partnerFirstName}.{' '}
                {!mirrorChecked
                  ? `Saving will unlink this account from ${possessive(partnerFirstName)} plan.`
                  : allocationOption === 'mirror'
                    ? 'Both will receive the full amount when you save.'
                    : (() => {
                        const pct = allocationOption === 'split-50-50' ? 50 : customClientPercent;
                        return `A ${pct}/${100 - pct} split will be applied when you save.`;
                      })()}
              </AlertDescription>
            </Alert>
          )}

          {careHasStarted && (
            <Alert
              variant="warning"
              className="gap-1! mb-4 rounded-md p-2.5 [&>svg]:translate-y-px"
            >
              <Info className="size-3.5!" />
              <AlertDescription className="text-xs">
                {isIncomeStream
                  ? "Care has already started, so the projection uses today's income and tax rate as-is. Pre-care growth assumptions no longer apply."
                  : "Care has already started, so the projection uses today's asset value as-is. Pre-care growth and contribution assumptions no longer apply. You can still record withdrawals for non-LTC expenses (rent, food, etc.)."}
              </AlertDescription>
            </Alert>
          )}

          <div
            className={cn('grid grid-cols-1 gap-4', {
              'grid-cols-2': isOther,
            })}
          >
            <div className="space-y-1">
              <FieldLabel
                className="text-xs"
                label="Account Type"
                tooltip="Select the type of account or asset to allocate for long-term care funding"
              />
              <Select
                value={selectedAssetType}
                onValueChange={handleAssetTypeChange}
                disabled={isReadOnlyRecipient}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  {filterForRetirementIncomeExpanded(
                    SELF_FUNDING_ASSET_TYPES,
                    flags.retirementIncomeExpanded,
                  ).map(t => {
                    const isUsed = usedAccountTypes.has(ASSET_TYPE_TO_ACCOUNT_TYPE[t.id]);
                    return (
                      <SelectItem
                        key={t.id}
                        value={t.id}
                        disabled={isUsed}
                        className="flex w-full items-center justify-between gap-2"
                      >
                        {t.label}
                        {isUsed && (
                          <Badge
                            variant="secondary"
                            className="px-1.5 py-0.5 text-[10px]"
                          >
                            Added
                          </Badge>
                        )}
                      </SelectItem>
                    );
                  })}
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isOther && (
              <div className="space-y-1">
                <FieldLabel
                  className="text-xs"
                  label="Name (optional)"
                  tooltip="Optional name to identify this self-funding source"
                />
                <Input
                  type="text"
                  placeholder="Self-funding"
                  value={selfFundingState.selfFundingName ?? ''}
                  onChange={e => handleChangeSelfFundingName(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Specific asset type fields (non-income-stream) */}
          {isSpecificAssetType && !isIncomeStream && fieldLabels && (
            <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
              <div className="space-y-1">
                <FieldLabel
                  className="text-xs"
                  label={fieldLabels.currentValue.label}
                  tooltip="Present-day value of this account or asset"
                  required={requiredKeys.includes('existingAssetsValue')}
                />
                <CurrencyInput
                  label=""
                  placeholder={fieldLabels.currentValue.placeholder}
                  value={selfFundingState.existingAssetsValue}
                  onChange={handleChangeSelfFunding('existingAssetsValue')}
                  onPreview={handleChangeSelfFunding('existingAssetsValue')}
                />
                <ErrorMessage message={formErrors.existingAssetsValue} />
              </div>

              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    className="text-xs"
                    label={fieldLabels.yearlyContribution.label}
                    tooltip="Amount added each year to this account"
                  />
                  <CurrencyInput
                    label=""
                    placeholder={fieldLabels.yearlyContribution.placeholder}
                    value={yearlyContributionValue}
                    onChange={handleChangeYearlyContribution}
                    onPreview={handleChangeYearlyContribution}
                  />
                </div>
              </DisabledByCareTooltip>

              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    className="text-xs"
                    label={fieldLabels.rateOfReturn.label}
                    tooltip="The expected annual rate of return applied to both existing assets and contributions"
                    required={requiredKeys.includes('annualRateOfReturn')}
                  />
                  <PercentInput
                    label=""
                    placeholder={fieldLabels.rateOfReturn.placeholder}
                    value={selfFundingState.annualRateOfReturn}
                    onChange={handleChangeSelfFunding('annualRateOfReturn')}
                    onPreview={handleChangeSelfFunding('annualRateOfReturn')}
                  />
                  <ErrorMessage message={formErrors.annualRateOfReturn} />
                </div>
              </DisabledByCareTooltip>

              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    className="text-xs"
                    label={fieldLabels.taxRate.label}
                    tooltip="The estimated tax rate applied when withdrawing from this account"
                    required={requiredKeys.includes('gainsTaxRate')}
                  />
                  <PercentInput
                    label=""
                    placeholder={fieldLabels.taxRate.placeholder}
                    value={selfFundingState.gainsTaxRate}
                    onChange={handleChangeSelfFunding('gainsTaxRate')}
                    onPreview={handleChangeSelfFunding('gainsTaxRate')}
                  />
                  <ErrorMessage message={formErrors.gainsTaxRate} />
                  {fieldLabels.taxRateHint && (
                    <p className="text-muted-foreground mt-1 text-[11px] leading-snug">
                      {fieldLabels.taxRateHint}
                    </p>
                  )}
                </div>
              </DisabledByCareTooltip>
            </div>
          )}

          {/* Income stream primary fields: annual income + tax rate */}
          {isIncomeStream && fieldLabels && (
            <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
              <div className="space-y-1">
                <FieldLabel
                  className="text-xs sm:min-h-8"
                  label={fieldLabels.currentValue.label}
                  tooltip="Expected annual income during long-term care years. If the reference age is earlier than LTC start age, the expected annual growth is applied to project the income forward."
                  required={requiredKeys.includes('existingAssetsValue')}
                />
                <CurrencyInput
                  label=""
                  placeholder={fieldLabels.currentValue.placeholder}
                  value={selfFundingState.existingAssetsValue}
                  onChange={handleChangeSelfFunding('existingAssetsValue')}
                  onPreview={handleChangeSelfFunding('existingAssetsValue')}
                />
                <ErrorMessage message={formErrors.existingAssetsValue} />
              </div>

              <div className="space-y-1">
                <FieldLabel
                  className="text-xs sm:min-h-8"
                  label={fieldLabels.taxRate.label}
                  tooltip="The estimated tax rate that applies to this income"
                  required={requiredKeys.includes('gainsTaxRate')}
                />
                <PercentInput
                  label=""
                  placeholder={fieldLabels.taxRate.placeholder}
                  value={selfFundingState.gainsTaxRate}
                  onChange={handleChangeSelfFunding('gainsTaxRate')}
                  onPreview={handleChangeSelfFunding('gainsTaxRate')}
                />
                <ErrorMessage message={formErrors.gainsTaxRate} />
              </div>
            </div>
          )}

          {/* Income stream: reference age + growth rate (side by side) */}
          {isIncomeStream && fieldLabels && flags.retirementIncomeExpanded && (
            <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    className="text-xs"
                    label="Income start age"
                    tooltip={
                      ltcAtAge != null
                        ? `The age at which the entered income applies. Defaults to LTC start age (${ltcAtAge}). If earlier, the Expected Annual Growth is applied each year up to LTC start age.`
                        : 'The age at which the entered income applies. If earlier than LTC start age, the Expected Annual Growth is applied each year up to LTC start age.'
                    }
                  />
                  <IntegerInput
                    label=""
                    placeholder={ltcAtAge != null ? String(ltcAtAge) : 'Age'}
                    value={selfFundingState.incomeReferenceAge ?? null}
                    onChange={value =>
                      setSelfFundingState(prev => ({ ...prev, incomeReferenceAge: value }))
                    }
                    onPreview={value =>
                      setSelfFundingState(prev => ({ ...prev, incomeReferenceAge: value }))
                    }
                  />
                  <ErrorMessage
                    message={incomeReferenceAgeError ?? formErrors.incomeReferenceAge}
                  />
                </div>
              </DisabledByCareTooltip>

              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    className="text-xs"
                    label={fieldLabels.rateOfReturn.label}
                    tooltip="The expected annual % increase in your retirement income, typically a cost-of-living adjustment, not an investment return. Compounds each year from the reference age through the end of the care period."
                  />
                  <PercentInput
                    label=""
                    placeholder={fieldLabels.rateOfReturn.placeholder}
                    value={selfFundingState.annualRateOfReturn}
                    onChange={value => handleChangeSelfFunding('annualRateOfReturn')(value ?? 0)}
                    onPreview={value => handleChangeSelfFunding('annualRateOfReturn')(value ?? 0)}
                  />
                </div>
              </DisabledByCareTooltip>
            </div>
          )}

          {/* "Other" type: all original fields */}
          {isOther && (
            <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
              <div className="space-y-1">
                <FieldLabel
                  label="Available Assets"
                  tooltip="Present-day value of assets that can be reserved for future long-term care expenses"
                  required={requiredKeys.includes('existingAssetsValue')}
                />
                <CurrencyInput
                  label=""
                  placeholder="Enter dollars"
                  value={selfFundingState.existingAssetsValue}
                  onChange={handleChangeSelfFunding('existingAssetsValue')}
                  onPreview={handleChangeSelfFunding('existingAssetsValue')}
                />
                <ErrorMessage message={formErrors.existingAssetsValue} />
              </div>

              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    label="Annual Rate of Return"
                    tooltip="The expected annual rate of return applied to both existing assets and monthly contributions"
                    required={requiredKeys.includes('annualRateOfReturn')}
                  />
                  <PercentInput
                    label=""
                    placeholder="Enter percent"
                    value={selfFundingState.annualRateOfReturn}
                    onChange={handleChangeSelfFunding('annualRateOfReturn')}
                    onPreview={handleChangeSelfFunding('annualRateOfReturn')}
                  />
                  <ErrorMessage message={formErrors.annualRateOfReturn} />
                </div>
              </DisabledByCareTooltip>

              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    label="Gains Tax Rate"
                    tooltip={
                      <>
                        The tax rate on investment profits. For more info, view the{' '}
                        <a
                          href="https://www.irs.gov/taxtopics/tc409"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          IRS article
                        </a>
                        .
                      </>
                    }
                    required={requiredKeys.includes('gainsTaxRate')}
                  />
                  <PercentInput
                    label=""
                    placeholder="Enter percent"
                    value={selfFundingState.gainsTaxRate}
                    onChange={handleChangeSelfFunding('gainsTaxRate')}
                    onPreview={handleChangeSelfFunding('gainsTaxRate')}
                  />
                  <ErrorMessage message={formErrors.gainsTaxRate} />
                </div>
              </DisabledByCareTooltip>

              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-1">
                  <FieldLabel
                    label="Monthly Contribution (optional)"
                    tooltip="Money added each month to assets or investments, aimed at increasing the total value available to fund long-term care expenses"
                  />
                  <CurrencyInput
                    label=""
                    placeholder="Enter dollars"
                    value={selfFundingState.monthlyContributionAmount}
                    onChange={handleChangeSelfFunding('monthlyContributionAmount')}
                    onPreview={handleChangeSelfFunding('monthlyContributionAmount')}
                  />
                </div>
              </DisabledByCareTooltip>

              <div className="space-y-1.5 sm:col-span-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <FieldLabel
                      label={
                        ltcAtAge != null
                          ? `Expected Monthly Income at LTC Start Age (${ltcAtAge})`
                          : 'Expected Monthly Income at LTC Start Age'
                      }
                      tooltip="Expected income during long-term care years, such as Social Security, pension, or rental income that can be allocated to LTC expenses. If the income schedule is not monthly, please calculate and enter the equivalent monthly amount."
                    />
                    <CurrencyInput
                      label=""
                      placeholder="Enter dollars"
                      value={selfFundingState.monthlyIncome}
                      onChange={handleChangeSelfFunding('monthlyIncome')}
                      onPreview={handleChangeSelfFunding('monthlyIncome')}
                    />
                  </div>

                  <div className="space-y-1">
                    <FieldLabel
                      label={
                        ltcAtAge != null
                          ? `Expected Annual Income Tax Rate at LTC Start Age (${ltcAtAge})`
                          : 'Expected Annual Income Tax Rate at LTC Start Age'
                      }
                      tooltip="The estimated tax rate that applies to your monthly income. This reduces the projected value to reflect after-tax income available for care expenses."
                    />
                    <PercentInput
                      label=""
                      placeholder="Enter percent"
                      value={selfFundingState.monthlyIncomeTaxRate ?? null}
                      onChange={handleChangeSelfFunding('monthlyIncomeTaxRate')}
                      onPreview={handleChangeSelfFunding('monthlyIncomeTaxRate')}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Advanced options: contribution stop + withdrawals */}
          {flags.selfFundingAdvancedOptions && !isIncomeStream && (
            <div className="mt-4 space-y-4">
              <DisabledByCareTooltip active={careHasStarted}>
                <div className="space-y-2 border-t pt-4">
                  <label
                    htmlFor="modal-has-contribution-stop"
                    className="flex cursor-pointer items-center justify-between"
                  >
                    <span className="inline-flex items-center gap-1 text-xs font-medium">
                      {fieldLabels?.contributionStop ?? 'End Contributions Early'}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="text-muted-foreground size-3.5 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-xs text-xs font-normal normal-case"
                        >
                          Set a year when assumed contributions to this account will stop, rather
                          than continuing until long-term care begins.
                        </TooltipContent>
                      </Tooltip>
                    </span>
                    <Switch
                      id="modal-has-contribution-stop"
                      checked={hasContributionStopToggle}
                      onCheckedChange={handleToggleContributionStop}
                    />
                  </label>

                  {hasContributionStopToggle && (
                    <YearPresetSelect
                      label="Stop at"
                      defaultLabel="At LTC start (default)"
                      value={selfFundingState.contributionStopYear}
                      onChange={year =>
                        setSelfFundingState(prev => ({ ...prev, contributionStopYear: year }))
                      }
                    />
                  )}
                </div>
              </DisabledByCareTooltip>

              <div className="space-y-2 border-t pt-4">
                <label
                  htmlFor="modal-has-drawdown"
                  className="flex cursor-pointer items-center justify-between"
                >
                  <span className="inline-flex items-center gap-1 text-xs font-medium">
                    Withdrawals
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="text-muted-foreground size-3.5 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-xs text-xs font-normal normal-case"
                      >
                        {careHasStarted
                          ? 'Withdrawals from this account for non-LTC expenses (rent, food, etc.). These reduce the value available for long-term care funding.'
                          : 'Expected withdrawals from this account before care begins. This reduces the projected value available for long-term care funding.'}
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <Switch
                    id="modal-has-drawdown"
                    checked={!!selfFundingState.hasDrawdown}
                    onCheckedChange={handleToggleDrawdown}
                  />
                </label>

                  {selfFundingState.hasDrawdown && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <YearPresetSelect
                          label="Withdrawals begin"
                          defaultLabel="Starting now"
                          value={selfFundingState.drawdownStartAtYear ?? null}
                          onChange={year =>
                            setSelfFundingState(prev => ({ ...prev, drawdownStartAtYear: year }))
                          }
                        />

                      <CarePhaseYearSelect
                        label="Withdrawals end"
                        defaultLabel={careHasStarted ? 'At LTC end' : 'At LTC start'}
                        value={selfFundingState.drawdownEndAtYear ?? null}
                        onChange={year =>
                          setSelfFundingState(prev => ({ ...prev, drawdownEndAtYear: year }))
                        }
                        carePhaseYears={carePhaseYears}
                        ltcEndYear={ltcEndYear}
                      />
                    </div>

                      <div className="space-y-1.5">
                        <Label className="inline-block text-xs font-medium">
                          Withdrawal amount
                        </Label>
                        <div className="flex items-center gap-2">
                          <InputGroup className="flex-1">
                            <InputGroupAddon align="inline-start">$</InputGroupAddon>
                            <InputGroupInput
                              inputMode="numeric"
                              placeholder="Enter amount"
                              value={
                                selfFundingState.drawdownAmount != null
                                  ? selfFundingState.drawdownAmount.toLocaleString('en-US')
                                  : ''
                              }
                              onChange={e => {
                                const raw = e.target.value.replace(/[^0-9]/g, '');
                                setSelfFundingState(prev => ({
                                  ...prev,
                                  drawdownAmount: raw ? Number(raw) : null,
                                }));
                              }}
                            />
                          </InputGroup>
                          <ToggleGroup
                            type="single"
                            value={selfFundingState.drawdownAmountInterval ?? 'yearly'}
                            onValueChange={val => {
                              if (val) {
                                setSelfFundingState(prev => ({
                                  ...prev,
                                  drawdownAmountInterval: val as 'monthly' | 'yearly',
                                }));
                              }
                            }}
                            variant="outline"
                            className="shrink-0"
                          >
                            <ToggleGroupItem value="monthly">Monthly</ToggleGroupItem>
                            <ToggleGroupItem value="yearly">Yearly</ToggleGroupItem>
                          </ToggleGroup>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          {showPartnerSection && !isReadOnlyRecipient && (
            <PartnerAllocationSection
              enabled={mirrorChecked}
              onEnabledChange={checked => {
                setMirrorChecked(checked);
                if (!checked && !isExistingMirror) {
                  setAllocationOption('mirror');
                  setCustomClientPercent(50);
                }
              }}
              value={totalAssets}
              clientName={clientName}
              partnerName={partnerFirstName ?? ''}
              selectedOption={allocationOption}
              onOptionChange={setAllocationOption}
              customClientPercent={customClientPercent}
              onCustomClientPercentChange={setCustomClientPercent}
            />
          )}

          {!formIsValid && (
            <FormErrorAlert mainErrorMessage="There is an error with your submission">
              {hasRequiredFieldsErrors && (
                <li>
                  <span className="font-bold">Required field:</span> Please fill out all required
                  fields.
                </li>
              )}
            </FormErrorAlert>
          )}
        </fieldset>
      )}
    </div>
  );

  const headerPreview = showPreview ? (
    <HeaderPreview
      isIncomeStream={isIncomeStream}
      existingAssetsValue={selfFundingState.existingAssetsValue}
      projectedValue={projectedValue}
      ageAtLtc={ageAtLtc}
      ltcAtYear={ltcAtYear}
      onShowFormula={() => setShowFormulaSheet(true)}
    />
  ) : null;

  const submitButton = isReadOnlyRecipient ? null : (
    <Button
      className="w-full"
      onClick={handleClickSubmit}
    >
      Submit
    </Button>
  );

  return (
    <>
      <UnsavedChangesDialog
        showWarning={showWarning}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
      />
      {isMobile ? (
        <Drawer
          open={open}
          onOpenChange={onClose}
        >
          <DrawerContent className="max-h-[90vh] overflow-y-auto px-4">
            <DrawerHeader className="text-left">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <DrawerTitle>Self-Funding Details</DrawerTitle>
                  <DrawerDescription>Configure your self-funding source</DrawerDescription>
                </div>
                {headerPreview}
              </div>
            </DrawerHeader>
            <div className="py-4">{formContent}</div>
            <DrawerFooter className="px-0">{submitButton}</DrawerFooter>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog
          open={open}
          onOpenChange={onClose}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 pr-8">
                <div>
                  <DialogTitle>Self-Funding Details</DialogTitle>
                  <DialogDescription>Configure your self-funding source</DialogDescription>
                </div>
                {headerPreview}
              </div>
            </DialogHeader>
            <div className="py-2">{formContent}</div>
            {submitButton}
          </DialogContent>
        </Dialog>
      )}

      <Sheet
        open={showFormulaSheet}
        onOpenChange={setShowFormulaSheet}
      >
        <SheetContent
          side="right"
          className="overflow-y-auto sm:max-w-lg"
        >
          <SheetHeader>
            <SheetTitle>{formulaInfo.title}</SheetTitle>
            <SheetDescription>{assetLabel}</SheetDescription>
          </SheetHeader>

          <CalcFormulaSheetContent
            assetTypeId={formulaAssetTypeId}
            label={assetLabel}
            hasContributions={
              isOther
                ? (selfFundingState.monthlyContributionAmount ?? 0) > 0
                : (yearlyContributionValue ?? 0) > 0
            }
            hasContributionStop={selfFundingState.contributionStopYear != null}
            hasWithdrawals={
              !!selfFundingState.hasDrawdown &&
              selfFundingState.drawdownAmount != null &&
              selfFundingState.drawdownAmount > 0
            }
            isIncomeStream={isIncomeStream}
            showsIncomeGrowth={flags.retirementIncomeExpanded === true}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
